import _ from 'lodash';

import {
  ActionStepContentType,
  ActionStepStatus,
  ActionStepType,
  AiChatPlanStepStatus,
  AiPlanStepKind,
  OPEN_LOAN_SECTION_AREA_LABEL,
} from 'constants/aiChat';
import { jsonParse } from 'utils/json';

import {
  actionPlanItemKind,
  resolveActionStepTypeFromToolName,
  resolveFriendlyToolLabel,
} from './ActionPlan.constants';
import { sanitizeStepErrorMessage } from './ActionPlan.utils';

const planStatusToActionStepStatus = {
  [AiChatPlanStepStatus.PENDING]: ActionStepStatus.PENDING,
  [AiChatPlanStepStatus.IN_PROGRESS]: ActionStepStatus.RUNNING,
  [AiChatPlanStepStatus.COMPLETED]: ActionStepStatus.SUCCESS,
  [AiChatPlanStepStatus.FAILED]: ActionStepStatus.ERROR,
  [AiChatPlanStepStatus.SKIPPED]: ActionStepStatus.SKIPPED,
  // DEV-2913: pause-on-user states. AWAITING_CONFIRMATION renders a
  // distinct "Awaiting confirmation" badge so the row reads as "paused on
  // me" rather than "spinning". CANCELLED reuses ActionStepStatus.CANCELLED
  // (existing icon from the legacy tool_cancelled flow).
  [AiChatPlanStepStatus.AWAITING_CONFIRMATION]: ActionStepStatus.AWAITING_CONFIRMATION,
  [AiChatPlanStepStatus.CANCELLED]: ActionStepStatus.CANCELLED,
};

const planKindToActionStepType = {
  [AiPlanStepKind.TOOL_CALL]: ActionStepType.EXECUTING_COMMAND,
  [AiPlanStepKind.MODEL_TURN]: ActionStepType.MODEL_TURN,
  [AiPlanStepKind.SPECIALIST]: ActionStepType.SPECIALIST,
};

// `kind` is only set on streamed events (plan_step_started carries it). The
// persisted plan blob carries `tool` + `description` per step but not `kind`,
// so on rehydration we'd otherwise lose the tool-specific icon + chip on every
// step. Always try to resolve from `toolName` first so streaming and
// post-refresh produce the same actionStepType for the same tool call.
const resolveActionStepType = step => {
  const fromToolName = resolveActionStepTypeFromToolName(step.toolName);

  if (fromToolName) {
    return fromToolName;
  }

  return planKindToActionStepType[step.kind] ?? null;
};

const parseStepArgs = argsPreview => {
  if (!_.isString(argsPreview) || argsPreview.length === 0) {
    return null;
  }

  try {
    const parsed = jsonParse(argsPreview);

    return _.isPlainObject(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

// `open_loan_section` is the only tool whose friendly label depends on its
// arguments — the area determines whether the row should read "Open Income
// Section" vs "Open Borrower Section". The `argsPreview` field carries a
// truncated JSON of the tool arguments (per BE `plan_step_tool_dispatched`).
const resolveOpenLoanSectionTitle = step => {
  const parsed = parseStepArgs(step.argsPreview);
  const area = parsed?.area;
  const areaLabel = area ? OPEN_LOAN_SECTION_AREA_LABEL[area] : null;

  if (_.isEmpty(areaLabel)) {
    return null;
  }

  return `Open ${areaLabel} Section`;
};

// DEV-2939: the bridged `plan_step_started` event for an advisory chat step
// sets `label` to the planner's `description` (see BE
// ModelTurnStepHandler.ResolveBridgeLabel, which returns
// ExecutionPlanStep.Description). When the live `plan_step_completed` then
// merges into store, the row's `label` equals its `description` — so a row
// that rendered a short friendly title while active/pending (label was null →
// friendly tool label) would suddenly show the long planner sentence as the
// title and lose its Why expansion (buildReasonContent bails when
// description === title). A label that is byte-identical to the description is
// therefore NOT a usable short title; treat it as absent so we fall through to
// the friendly tool label, exactly as the rehydration path does (the persisted
// blob carries `label = null`). This keeps the live-completed render identical
// to both the active render and the post-refresh render.
const isUsableLabel = step =>
  _.isString(step.label) && step.label.length > 0 && step.label !== step.description;

// The streaming `plan_step_started` event ships a short, friendly `label`
// (e.g. "Reading borrower information"). The persisted blob doesn't carry
// `label`, only `tool` + `description`, so on rehydration we synthesize the
// same label from the tool name via the FE-side mirror of BE's
// ToolFriendlyNameMapper. This keeps the title row identical between live
// streaming and post-refresh, and preserves the description for the Why
// expansion below.
const resolvePlanStepTitle = step => {
  if (step.toolName === 'open_loan_section') {
    const synthesized = resolveOpenLoanSectionTitle(step);

    if (!_.isNil(synthesized)) {
      return synthesized;
    }
  }

  if (isUsableLabel(step)) {
    return step.label;
  }

  const friendlyToolLabel = resolveFriendlyToolLabel(step.toolName);

  if (!_.isNil(friendlyToolLabel)) {
    return friendlyToolLabel;
  }

  if (_.isString(step.description) && step.description.length > 0) {
    return step.description;
  }

  return null;
};

const buildErrorContent = (step, baseId) => {
  if (step.status !== AiChatPlanStepStatus.FAILED || _.isNil(step.error)) {
    return null;
  }

  return {
    id: `${baseId}-error`,
    contentType: ActionStepContentType.ERROR,
    contentProps: {
      errorMessage: sanitizeStepErrorMessage(step.error),
    },
  };
};

const buildScannedSectionsContent = (step, baseId) => {
  if (!_.isArray(step.scannedSections) || step.scannedSections.length === 0) {
    return null;
  }

  return {
    id: `${baseId}-scanned-sections`,
    contentType: ActionStepContentType.SCANNED_SECTIONS,
    contentProps: {
      scannedSections: step.scannedSections,
    },
  };
};

const resolveToolCallLabel = (call, index) => {
  const parsed = parseStepArgs(call.argsPreview);
  const sectionNumber = parsed?.section_number ?? parsed?.sectionNumber;

  if (!_.isNil(sectionNumber)) {
    return `§${sectionNumber}`;
  }

  if (_.isString(call.argsPreview) && call.argsPreview.length > 0) {
    return call.argsPreview;
  }

  return `Call ${index + 1}`;
};

// The BE reuses one stepKey across every invocation of the same tool (e.g.
// get_guideline_section reading N sections), so the reducer accumulates them
// into `toolCalls`. Surface the per-call breakdown only when more than one
// invocation occurred — a single call is already represented by the row itself.
const buildToolCallsContent = (step, baseId) => {
  if (!_.isArray(step.toolCalls) || step.toolCalls.length <= 1) {
    return null;
  }

  return {
    id: `${baseId}-tool-calls`,
    contentType: ActionStepContentType.TOOL_CALLS,
    contentProps: {
      toolCalls: step.toolCalls.map((call, index) => ({
        id: `${baseId}-tool-call-${index}`,
        label: resolveToolCallLabel(call, index),
        detail: _.isString(call.outputPreview) ? call.outputPreview : null,
      })),
    },
  };
};

// When the planner provides both a short friendly `label` AND a longer
// `description`, the label becomes the row title and the description is the
// reasoning shown on expand. If only one of them exists it already serves as
// the title, so there's nothing useful to add to the expanded body.
const buildReasonContent = (step, baseId, title) => {
  if (!_.isString(step.description) || step.description.length === 0) {
    return null;
  }

  if (step.description === title) {
    return null;
  }

  return {
    id: `${baseId}-reason`,
    contentType: ActionStepContentType.STEP_SUMMARY,
    contentProps: {
      summary: step.description,
    },
  };
};

const resolveTitleWithCallCount = (step, title) => {
  const callCount = _.isArray(step.toolCalls) ? step.toolCalls.length : 0;

  if (callCount <= 1 || _.isNil(title)) {
    return title;
  }

  return `${title} (${callCount})`;
};

export const planStepToActionPlanItem = step => {
  const baseId = step.stepKey ?? step.id ?? `step-${step.stepIndex}`;
  const status = planStatusToActionStepStatus[step.status] ?? ActionStepStatus.PENDING;
  const actionStepType = resolveActionStepType(step);
  const baseTitle = resolvePlanStepTitle(step);
  const title = resolveTitleWithCallCount(step, baseTitle);

  const content = [
    buildReasonContent(step, baseId, baseTitle),
    buildToolCallsContent(step, baseId),
    buildScannedSectionsContent(step, baseId),
    buildErrorContent(step, baseId),
  ].filter(Boolean);

  return {
    kind: actionPlanItemKind.STEP,
    id: baseId,
    stepKey: step.stepKey ?? null,
    title,
    status,
    actionStepType,
    content,
    dependsOn: step.dependsOn ?? [],
    isNewSinceReplan: !!step.isNewSinceReplan,
    superseded: !!step.superseded,
    toolName: step.toolName ?? null,
    error: step.error ?? null,
  };
};
